import React, { createContext, useContext, useState, useCallback } from 'react';

const NestedSubGroupContext = createContext();

/**
 * The selected nested sub data group chain, below "Sub Data Group 1".
 *
 * `nestedChain[0]` is the value of "Sub Data Group 2", `[1]` of "Sub Data Group
 * 3", and so on. Only the selection lives here: the options for each level and
 * whether a level has anything to show are derived from the already-loaded
 * entity lists (see utils/general/subGroupFilter), and no level can be loading
 * or failing because selecting one performs no I/O.
 */
export function NestedSubGroupProvider({ children }) {
  const [nestedChain, setNestedChain] = useState([]);

  /** Select at a level, dropping every deeper level's selection. */
  const selectAtLevel = useCallback((levelIndex, value) => {
    setNestedChain((prev) => {
      const next = prev.slice(0, levelIndex);
      if (value) next[levelIndex] = value;
      return next;
    });
  }, []);

  /** Clear a level and everything below it (the level's clear icon). */
  const clearFromLevel = useCallback((levelIndex) => {
    setNestedChain((prev) => prev.slice(0, levelIndex));
  }, []);

  const clearAllLevels = useCallback(() => {
    setNestedChain([]);
  }, []);

  return (
    <NestedSubGroupContext.Provider
      value={{
        nestedChain,
        setNestedChain,
        selectAtLevel,
        clearFromLevel,
        clearAllLevels,
      }}
    >
      {children}
    </NestedSubGroupContext.Provider>
  );
}

export function useNestedSubGroup() {
  return useContext(NestedSubGroupContext);
}
